import { createSelector } from '@reduxjs/toolkit';

import { getProductValidationValues } from 'user/selectors';
import { calculateDensityCurve, convertUnitsOfDensityCurve } from './util/densityCurves';
import { convert } from 'util/unitConversion';
import { sortByArea } from 'util/sort';
import type { Store, Script, User, Scenario } from 'store/types/types';
import type { DensityCurve } from './util/densityCurves';
import { UNIT_TYPES } from 'constants/unitTypes';
import { CURVES_EXPANSION_RATE, RATIOS } from 'constants/advanced';
import { getAverageTargetYieldFromZones } from './util/getAverageTargetYieldFromZones';

type ZonesDensityCurves = {
  [index: string]: DensityCurve;
};

export const getScenarios: (state: Store) => Store['scenarios'] = state => state.scenarios;
export const getScripts: (state: Store) => Store['scripts'] = state => state.scripts;
export const getCrops: (state: Store) => Store['crops'] = state => state.crops;
export const getAllScenarios = (state: Store) => state.scenarios.allScenarios;
export const getActiveScenario = createSelector(
  [(state: Store) => state.scenarios.activeScenarioUuid, getAllScenarios],
  (activeScenarioUuid, allScenarios) =>
    allScenarios.find(({ scenarioUuid }) => scenarioUuid === activeScenarioUuid)
);

export const getActiveScenarioScript: (state: Store) => Script = createSelector(
  [getActiveScenario, getScripts], (scenario, scripts) => {
    return scripts[scenario.scenarioUuid];
  });

export const getOriginalScenario = createSelector(
  [(state: Store) => state.scenarios.originalScenarioUuid, getAllScenarios],
  (originalScenarioUuid, allScenarios) => 
    allScenarios.find(({ scenarioUuid }) => scenarioUuid === originalScenarioUuid)
);

export const getYourPlanScenario = createSelector(
  [(state: Store) => state.scenarios.yourPlanScenarioUuid, getAllScenarios],
  (yourPlanScenarioUuid, allScenarios) => 
    allScenarios.find(({ scenarioUuid }) => scenarioUuid === yourPlanScenarioUuid)
);
  
/**
 * Gets the nominal weight of the selecte crop on the currrent script.
 */
export const getScriptCropNominalWeight: (state: Store) => number
  = createSelector([getActiveScenario, getScripts, getCrops], (scenario, scripts, crops) => {
    const script = scripts[scenario.scenarioUuid];
    const cropId = script.meta.cropId;
    return crops[cropId]['nominal-weight'].q;
  });

export const getCurvesBounds = createSelector(
  [getAllScenarios, getProductValidationValues],
  (allScenarios, productValidationValues: User['productValidationValues']) => {
    const allRatesSorted = allScenarios
    .reduce((rates: number[], scenario) => {
      const scenarioRates = Object.keys(scenario.zones).map(
        (zoneId) => scenario.zones[zoneId].meta.applications.seed[0].rate.q
      );

      return [...rates, ...scenarioRates];
    }, [])
    .sort((rateA, rateB) => rateA - rateB);

    const crop = allScenarios.at(0).crop.toUpperCase() as Uppercase<Scenario['crop']>;
    const { RATE: { max, unit } } = productValidationValues.PLANTING[crop];
    const imperialMax = convert({
      from: { q: max, u: unit },
      to: { u: UNIT_TYPES.seed_ac }
    }).q;

    const imperialMin = imperialMax * RATIOS.MIN_RATE;
    const highestRate = allRatesSorted.at(-1);
    const lowestRate = allRatesSorted.at(0);
    /** 
     * We need to have dynamic boundaries for the curves that depend on the seeding rates in
     * scenarios and zones. For that, we use a constant (CURVES_EXPANSION_RATE) which 
     * represents a percentage that will be applied to the minimum (LOWER) and the maximum (UPPER) 
     * rates to extend the curves from those values.
     * At the same time we want to modify that percentage in an inverse proportion to the 
     * difference between the maximum and the minimum. So if both are equals we extend the 
     * lines adding the percentage to the max and subtracting it to the min. But we reduce 
     * that percentage if the difference increases.
     * We use the inverse proportion formula: for highestRate / lowestRate = 1 -which 
     * implies no difference between them- we use CURVES_EXPANSION_RATE. Other values are calculated
     * with CURVES_EXPANSION_RATE / (highestRate / lowestRate)
    */
    const lowerBoundaryRate = CURVES_EXPANSION_RATE.LOWER / (highestRate / lowestRate);
    const upperBoundaryRate = CURVES_EXPANSION_RATE.UPPER / (highestRate / lowestRate);

    const lowerBound = Math.min(lowestRate, Math.max(lowestRate * (1 - lowerBoundaryRate), imperialMin));
    const upperBound = Math.max(highestRate, Math.min(highestRate * (1 + upperBoundaryRate), imperialMax));

    return [lowerBound, upperBound] as [number, number];
  }
);

/**
 * Generates the density curve for the entire field on the current scenario based on the Hardin model.
 */
export const getFieldDensityCurve: (state: Store) => DensityCurve
  = createSelector([
    getActiveScenario,
    getScripts,
    getScriptCropNominalWeight,
    getCurvesBounds
  ], 
  (scenario, scripts, cropNominalWeight, curvesBounds) => {
    const script = scripts[scenario.scenarioUuid];
    const { hardinParams: parameters} = script.densityCurveField;

    const { q: targetYield} = getAverageTargetYieldFromZones(scenario.zones);
    const [metricLowerBound, metricUpperBound] = curvesBounds.map(
      (bound) =>
        convert({
          from: { q: bound, u: UNIT_TYPES.seed_ac },
          to: { u: UNIT_TYPES.seed_ha }
        }).q
    );
  
    return convertUnitsOfDensityCurve(
      calculateDensityCurve({ 
        targetYield,
        hardinParamsA: parameters.hardinParamsA,
        hardinParamsB: parameters.hardinParamsB,
        lowerBound: metricLowerBound,
        upperBound: metricUpperBound,
        yieldLabel: 'fieldAverageYield'
      }),
      cropNominalWeight);
  });

/**
 * Generates the density curve for all zones on the current scenario based on the Hardin model.
 */
export const getZonesDensityCurves: (state: Store) => ZonesDensityCurves
  = createSelector([
    getActiveScenario,
    getScripts,
    getScriptCropNominalWeight,
    getCurvesBounds
  ],
  (scenario, scripts, cropNominalWeight, curvesBounds) => {
    const script = scripts[scenario.scenarioUuid];
    const { hardinParams: parameters } = script.densityCurveField;
    const [metricLowerBound, metricUpperBound] = curvesBounds.map(
      (bound) =>
        convert({
          from: { q: bound, u: UNIT_TYPES.seed_ac },
          to: { u: UNIT_TYPES.seed_ha }
        }).q
    );

    const zonesDensityCurves = Object.values(scenario.zones).sort(sortByArea).reduce((curves, zone, index) => {
      const result = {
        // The curves are organized by their id in the response.
        [zone.meta.zoneId]: convertUnitsOfDensityCurve(
          calculateDensityCurve({
            // We need to use the highest yield of every zone to generate the curves.
            targetYield: zone.meta.highestYield.estimatedYield.q,
            hardinParamsA: parameters.hardinParamsA,
            hardinParamsB: parameters.hardinParamsB,
            lowerBound: metricLowerBound,
            upperBound: metricUpperBound,
            yieldLabel: `zone-${index + 1}-yield`
          }),
          cropNominalWeight)
      };
      return { ...result, ...curves };
    }, {} as ZonesDensityCurves);
    return zonesDensityCurves;
  });

export const isActiveScenario = createSelector([
  (state: Store) => state.scenarios.activeScenarioUuid,
  (_, scenarioUuid: string) => scenarioUuid
],
(activeScenarioUuid, scenarioUuid) => activeScenarioUuid === scenarioUuid
);

export const isOriginalScenario = createSelector([
  (state: Store) => state.scenarios.originalScenarioUuid,
  (_, scenarioUuid: string) => scenarioUuid
],
(originalScenarioUuid, scenarioUuid) => originalScenarioUuid === scenarioUuid
);
